import type {
  LightningConfig,
  LightningCycleShape,
  LightningGroup,
} from './config/types';
import { groupDuration, shuffleGroups } from './timing';

// The stagger window used when a config does not carry one. Small on
// purpose: an "all at once" cycle is meant to overlap heavily, not queue up.
export const DEFAULT_STAGGER_MIN = 0;
export const DEFAULT_STAGGER_MAX = 0.25;

export const createPlayback = () => ({
  // -- LEGACY BAG MODE: one group at a time, each cycle its own turn.
  groupId: null as string | null,
  age: 0,
  seed: 0,
  rest: 0,
  bag: [] as string[],
  // -- CYCLE MODE: every group laid out inside one shared cycle.
  //
  // `offsets` and `seeds` are re-rolled once per cycle rather than per
  // frame, so the Maps allocate on a cycle boundary and never in useFrame.
  concurrent: false,
  // WHICH SHAPE THIS CYCLE DREW. Re-rolled per cycle when the config asks
  // for 'auto', so the strike is sometimes one simultaneous hit and
  // sometimes a run of separate ones, without anyone toggling anything.
  shape: 'together' as LightningCycleShape,
  // Forces the NEXT cycle's shape once, then clears itself. Set by the
  // caller when playback is created -- see EngineLightning's openingShape.
  openingShape: null as LightningCycleShape | null,
  // SUSTAIN RATHER THAN CYCLE. Written by the caller every frame; while it
  // is true no cycle ever expires and each group is re-cued the moment it
  // burns out, so the strike never lets up. See advanceCycle.
  holding: false,
  // Internal: lets advanceCycle notice the release and hand back to normal
  // cycling without cutting off whatever is still in flight.
  wasHolding: false,
  cycleAge: 0,
  cycleLength: 0,
  offsets: new Map<string, number>(),
  seeds: new Map<string, number>(),
});

export type LightningPlayback = ReturnType<typeof createPlayback>;

// Clamped the same way the original rest window was: negatives floor to 0,
// and an inverted pair (min > max) still yields a sane range.
const randomBetween = (a: number, b: number, random: () => number): number => {
  const low = Math.max(0, Math.min(a, b));
  const high = Math.max(low, a, b);
  return low + random() * (high - low);
};

const eligibleGroups = (config: LightningConfig): LightningGroup[] =>
  config.groups.filter((group) => group.enabled && groupDuration(group) > 0);

/**
 * LEGACY BAG PLAYBACK: pick one group, hold it for its own length plus a
 * random rest, then move to the next from a shuffled bag.
 *
 * No longer how the lab drives anything -- 'one-at-a-time' below does the
 * same thing INSIDE a cycle, which is what lets the aura stay locked to it.
 * Kept because it is the one mode with no cycle at all, and because a bag
 * that visits every position before repeating is worth not losing.
 */
export const advancePlayback = (
  state: LightningPlayback,
  config: LightningConfig,
  delta: number,
  random = Math.random
) => {
  // The scheduler that ran owns the mode flag, so Bolt reads a state that
  // cannot disagree with how it was advanced.
  state.concurrent = false;
  const eligible = eligibleGroups(config);
  const ids = eligible.map((group) => group.id);
  if (!ids.length) {
    state.groupId = null;
    state.bag = [];
    return;
  }
  state.age += Math.max(0, delta);
  const current = eligible.find((group) => group.id === state.groupId);
  if (current && state.age < groupDuration(current) + state.rest) return;
  state.bag = state.bag.filter((id) => ids.includes(id));
  if (!state.bag.length) state.bag = shuffleGroups(ids, state.groupId, random);
  state.groupId = state.bag.shift() ?? null;
  state.age = 0;
  state.seed = random() * 1000;
  state.rest = randomBetween(config.gapMin, config.gapMax, random);
};

// 'auto' is a coin flip per cycle. Anything else is taken literally, so a
// caller that wants one shape forever still gets it.
//
// An `openingShape` on the state outranks both, once, and is then spent --
// that is how the Intro opens on every group at once as the engine settles
// and only afterwards starts rolling.
const pickShape = (
  state: LightningPlayback,
  config: LightningConfig,
  random: () => number
): LightningCycleShape => {
  const opening = state.openingShape;
  if (opening) {
    state.openingShape = null;
    return opening;
  }
  const shape = config.cycleShape ?? 'auto';
  if (shape !== 'auto') return shape;
  return random() < 0.5 ? 'together' : 'one-at-a-time';
};

/**
 * WHERE EACH GROUP SITS INSIDE THE CYCLE, measured from the first cue.
 *
 * Both shapes are nothing more than a different offset layout -- which is
 * the whole reason 'one-at-a-time' can live inside a cycle rather than
 * needing its own scheduler, and why the cycle's length simply follows from
 * whichever was drawn.
 */
const layOutCycle = (
  state: LightningPlayback,
  eligible: LightningGroup[],
  config: LightningConfig,
  random: () => number
): { relative: Map<string, number>; span: number } => {
  const relative = new Map<string, number>();
  let span = 0;

  if (state.shape === 'together') {
    for (const group of eligible) {
      const at = randomBetween(
        config.staggerMin ?? DEFAULT_STAGGER_MIN,
        config.staggerMax ?? DEFAULT_STAGGER_MAX,
        random
      );
      relative.set(group.id, at);
      span = Math.max(span, at + groupDuration(group));
    }
    return { relative, span };
  }

  // ONE AT A TIME: each group waits out the one before it, in a shuffled
  // order that avoids opening on whichever group closed the last cycle.
  const ids = eligible.map((group) => group.id);
  const order = shuffleGroups(ids, state.groupId, random);
  let cursor = 0;
  for (const id of order) {
    const group = eligible.find((item) => item.id === id);
    if (!group) continue;
    relative.set(id, cursor);
    span = Math.max(span, cursor + groupDuration(group));
    cursor +=
      groupDuration(group) +
      randomBetween(config.gapMin, config.gapMax, random);
  }
  // Remembered so the next shuffled cycle does not repeat it first.
  state.groupId = order[order.length - 1] ?? state.groupId;
  return { relative, span };
};

// Re-rolls this cycle's shape, every group's start offset, and every group's
// jag seed.
//
// THE FIRST BOLT LANDS AT THE TOP OF THE CYCLE. There is no lead-in: an
// earlier version held the whole first half of every cycle empty so an
// aura could swell into it, which on a one-at-a-time roll meant several
// seconds of nothing before anything struck. The aura runs on its own clock
// now and this waits for nobody.
const rollCycle = (
  state: LightningPlayback,
  eligible: LightningGroup[],
  config: LightningConfig,
  random: () => number
) => {
  state.offsets.clear();
  state.seeds.clear();
  state.shape = pickShape(state, config, random);

  const { relative, span } = layOutCycle(state, eligible, config, random);

  for (const group of eligible) {
    state.offsets.set(group.id, relative.get(group.id) ?? 0);
    // Per GROUP rather than per line: strands inside one group should keep
    // their relative shapes, which Bolt varies further by its own lineSeed.
    state.seeds.set(group.id, random() * 1000);
  }
  // The cycle is the strike plus a random rest -- so the pause between one
  // strike and the next varies, while the strike itself never waits.
  state.cycleLength =
    span + randomBetween(config.gapMin, config.gapMax, random);
  state.cycleAge = 0;
};

/**
 * CYCLE PLAYBACK: one shared cycle, re-shaped every time it comes round.
 *
 * Each cycle independently draws whether the groups strike TOGETHER -- all
 * cued within the stagger window, heavily overlapped -- or ONE AT A TIME,
 * each waiting out the one before it. Neither is a mode anyone switches: the
 * variation is the point, so a run of strikes never settles into a pattern.
 *
 * THE AURA IS NOT INVOLVED. It breathes on its own clock, and this waits
 * for nothing -- the two are deliberately independent, because tying them
 * together meant holding the frame empty until the ring was ready.
 *
 * A group enabled MID-CYCLE has no offset yet and is treated as 0 until the
 * next roll -- see the `?? 0` where Bolt reads it.
 */
export const advanceCycle = (
  state: LightningPlayback,
  config: LightningConfig,
  delta: number,
  random = Math.random
) => {
  // See the note in advancePlayback: the scheduler owns the mode flag.
  state.concurrent = true;
  const eligible = eligibleGroups(config);
  if (!eligible.length) {
    state.offsets.clear();
    state.seeds.clear();
    state.cycleAge = 0;
    state.cycleLength = 0;
    return;
  }
  state.cycleAge += Math.max(0, delta);

  // -- THE HOLD: a sustain, not a cycle.
  //
  // Every group is kept alight, and the instant one burns out it is re-cued
  // then and there with a fresh seed. Nothing waits for a cycle boundary
  // and nothing rests, so the strike reads as continuous rather than as a
  // flash that has to be caught. The stagger window still applies, which is
  // what keeps the re-cues from lining up into a pulse.
  //
  // WHY RE-CUE PER GROUP rather than replaying the whole cycle: the groups
  // are different lengths, so a shared restart would leave the frame
  // thinning out before every repeat -- visible as exactly the blink this
  // is meant to remove.
  //
  // A CONTINUOUS config runs this same path permanently, with one change:
  // a group that has already played rests for `gapMin`..`gapMax` before
  // re-firing, unless that would leave fewer than `minLive` groups alight.
  const continuous = config.continuous;
  if (state.holding || continuous) {
    state.shape = 'together';
    const isLive = (group: LightningGroup) => {
      const offset = state.offsets.get(group.id);
      return (
        offset !== undefined &&
        offset <= state.cycleAge &&
        state.cycleAge < offset + groupDuration(group)
      );
    };
    let live = eligible.filter(isLive).length;
    for (const group of eligible) {
      const offset = state.offsets.get(group.id);
      const isSpent =
        offset === undefined || state.cycleAge >= offset + groupDuration(group);
      if (!isSpent) continue;
      const hasPlayed = offset !== undefined;
      let wait =
        continuous && hasPlayed
          ? randomBetween(config.gapMin, config.gapMax, random)
          : randomBetween(
              config.staggerMin ?? DEFAULT_STAGGER_MIN,
              config.staggerMax ?? DEFAULT_STAGGER_MAX,
              random
            );
      if (continuous && live < continuous.minLive) wait = 0;
      if (wait === 0) live++;
      state.offsets.set(group.id, state.cycleAge + wait);
      state.seeds.set(group.id, random() * 1000);
    }
    state.wasHolding = state.holding;
    return;
  }

  // -- THE RELEASE. Whatever is mid-strike is allowed to finish; only then
  // does the rest begin and normal cycling resume. Cutting straight to a
  // fresh cycle here would clip live strands mid-flight.
  if (state.wasHolding) {
    state.wasHolding = false;
    state.cycleLength =
      Math.max(
        state.cycleAge,
        ...eligible.map(
          (group) => (state.offsets.get(group.id) ?? 0) + groupDuration(group)
        )
      ) + randomBetween(config.gapMin, config.gapMax, random);
    return;
  }

  // cycleLength 0 means nothing has been rolled yet, so the first frame
  // starts a cycle rather than waiting out a zero-length one.
  if (state.cycleLength > 0 && state.cycleAge < state.cycleLength) return;
  rollCycle(state, eligible, config, random);
};
